import { Sun, Moon, Clock } from "lucide-react";

interface AlmanacProps {
  sunrise: number;
  sunset: number;
  moonrise?: number;
  moonset?: number;
  moonPhase?: number;
}

function formatTime(ts?: number) {
  if (!ts) return "--";
  return new Date(ts * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function dayLength(sunrise: number, sunset: number): string {
  const seconds = sunset - sunrise;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function moonPhaseLabel(phase: number): { label: string; icon: string } {
  if (phase === 0 || phase === 1) return { label: "New Moon", icon: "🌑" };
  if (phase < 0.25) return { label: "Waxing Crescent", icon: "🌒" };
  if (phase === 0.25) return { label: "First Quarter", icon: "🌓" };
  if (phase < 0.5) return { label: "Waxing Gibbous", icon: "🌔" };
  if (phase === 0.5) return { label: "Full Moon", icon: "🌕" };
  if (phase < 0.75) return { label: "Waning Gibbous", icon: "🌖" };
  if (phase === 0.75) return { label: "Last Quarter", icon: "🌗" };
  return { label: "Waning Crescent", icon: "🌘" };
}

export function Almanac({ sunrise, sunset, moonrise, moonset, moonPhase = 0 }: AlmanacProps) {
  const phase = moonPhaseLabel(moonPhase);

  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <h3 className="mb-3 text-sm font-semibold flex items-center gap-2">
        <Clock className="h-4 w-4 text-primary" />
        Almanac
      </h3>

      <div className="space-y-3">
        {/* Sun */}
        <div className="rounded-lg bg-muted/30 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Sun className="h-3.5 w-3.5 text-amber-400" />
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Sun</span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div>
              <p className="text-[10px] text-muted-foreground">Rise</p>
              <p className="font-mono font-semibold">{formatTime(sunrise)}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground">Set</p>
              <p className="font-mono font-semibold">{formatTime(sunset)}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground">Length</p>
              <p className="font-mono font-semibold">{dayLength(sunrise, sunset)}</p>
            </div>
          </div>
        </div>

        {/* Moon */}
        <div className="rounded-lg bg-muted/30 p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Moon className="h-3.5 w-3.5 text-slate-300" />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Moon</span>
            </div>
            <span className="text-base">{phase.icon}</span>
          </div>
          <p className="text-xs font-semibold mb-2">{phase.label}</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <p className="text-[10px] text-muted-foreground">Rise</p>
              <p className="font-mono font-semibold">{formatTime(moonrise)}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground">Set</p>
              <p className="font-mono font-semibold">{formatTime(moonset)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
